import { SyncClient } from "client/Api/SharePointSettings/sharepointSettings";
import {
	BusyIndicator,
	ChangeTriggerKind
} from "client/Components";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	getActionsForLiveTile,
	getOpenActions,
	OpenActionHandlers,
	SharePointDocumentAction,
	SharePointDocumentActionContext
} from "client/Features/Documents/SharePoint/actions";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice
} from "client/Hooks";
import {
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	DocumentItemType,
	DocumentsViewModel,
	GridFolderContentsModel,
	DocumentItemViewModel,
	ListSortDirection,
	PagePosition,
	QueryParametersModel,
	SearchScope,
	SharePointColumnId
} from "client/Api/Documents/SharePoint/common";
import {
	getFolderContent,
	getRootContent,
	getViewAndDocumentItemModelForLocation
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SharePointSpecificFolder } from "client/Api/Workspace/LiveInformation/Documents/documentsLiveInformation";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../LiveTileContent";
import { getSharePointLocationUrl } from "client/Features/Documents/routing";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { DocumentListItem } from "./DocumentListItem";
import { DimensionToRender } from "../../RenderState/common";

export type SharePointFolderTabProps = {
	appIcon: AppImageDisplayInfo;
	tabSelector: ReactElement;
	name: string;
	destinationWindow: DestinationWindow;
	dimension: DimensionToRender;
	folder: SharePointSpecificFolder;
	addAnalyticsEvent: () => void;
};

export const SharePointFolderTab = (props: SharePointFolderTabProps): ReactElement => {
	const {
		appIcon,
		tabSelector,
		name,
		destinationWindow,
		folder,
		dimension,
		addAnalyticsEvent
	} = props;

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			const {
				siteUrl,
				listId,
				id
			} = folder;

			const url: string = getSharePointLocationUrl(
				{ siteUrl, listId },
				id
			);

			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			addAnalyticsEvent,
			destinationWindow,
			folder
		]
	);

	const shouldShowQuickActions: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	return (
		<>
			<LiveTileContent.Header icon={appIcon}>
				{tabSelector}
			</LiveTileContent.Header>
			<LiveTileContent.Content>
				<SharepointFolderContent
					folder={folder}
					onAddAnalyticsEvent={addAnalyticsEvent}
					destinationWindow={destinationWindow}
					shouldShowQuickActions={shouldShowQuickActions}
				/>
			</LiveTileContent.Content>
			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</>
	);
};
SharePointFolderTab.displayName = "SharePointFolderTab";

type SharepointFolderContentProps = {
	folder: SharePointSpecificFolder;
	onAddAnalyticsEvent: () => void;
	destinationWindow: DestinationWindow;
	shouldShowQuickActions: boolean;
};

const SharepointFolderContent = (props: SharepointFolderContentProps): ReactElement => {
	const {
		folder,
		onAddAnalyticsEvent,
		destinationWindow,
		shouldShowQuickActions
	} = props;

	const [viewAndFolder, setViewAndFolder] = useState<OptionalModel<ViewAndFolder | null>>({
		isSet: false,
	});

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const loadPageCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);

	const { isConfigured: isSharePointConfigured } = documentsConfiguration().sharePoint;
	const alertManager: AlertManager = useAlerts();
	const isTouch: boolean = useIsTouchDevice();

	const {
		siteUrl,
		listId,
		id
	} = folder;

	useEffect(
		(): (() => void) | void => {
			setViewAndFolder({
				isSet: false
			});

			if (!isSharePointConfigured) {
				setLoadingState({
					isLoading: false,
					hasError: true
				});

				return;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					const { view, documentItem } = await getViewAndDocumentItemModelForLocation(
						convertToLocationModel({ siteUrl, listId }),
						id,
						signal
					);

					if (isNotNil(documentItem)
						&& documentItem.itemType !== DocumentItemType.Folder
					) {
						console.error(`Expected a folder but got an '${documentItem.itemType}' instead`);

						setLoadingState({
							isLoading: false,
							hasError: true
						});

						return;
					}

					let folderToUse: Folderish;
					if (documentItem) {
						folderToUse = { ...documentItem, isRoot: false };
					} else {
						folderToUse = {
							isRoot: true,
							id: null,
							name: view.title,
							viewId: view.id,
							location: view.location
						};
					}

					setViewAndFolder({
						isSet: true,
						value: {
							view,
							folder: folderToUse,
						}
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setViewAndFolder({
						isSet: true,
						value: null
					});

					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			id,
			isSharePointConfigured,
			listId,
			siteUrl
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);

			scrollableRef?.current
				?.scrollTo(0);
		},
		[searchTerm]
	);

	const loadPageHandler = useCallback(
		async (
			viewAndFolder: ViewAndFolder,
			searchTerm: string,
			pagePosition: PagePosition | null
		): Promise<void> => {
			loadPageCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			loadPageCancellationRef.current = cancellation;

			setLoadingState(
				(x): LoadingState => {
					const retrievedData: RetrievedData | undefined = pagePosition && (x.isLoading || !x.hasError)
						? x.retrievedData
						: undefined;

					return {
						isLoading: true,
						retrievedData
					};
				}
			);

			const { folder } = viewAndFolder;

			const queryParameters: QueryParametersModel = {
				itemsCount: PAGE_SIZE,
				sortingField: SharePointColumnId.name,
				sortingDirection: ListSortDirection.Ascending,
				pagePosition,
				filterText: searchTerm,
				searchScope: SearchScope.CurrentFolder,
				fileTypeFilter: null
			};

			try {
				let page: GridFolderContentsModel;

				if (folder.isRoot === true) {
					page = await getRootContent(
						{
							location: convertToLocationModel(folder.location),
							queryParameters: queryParameters
						},
						cancellation.signal
					);
				} else {
					page = await getFolderContent(
						{
							id: folder.id,
							folderLocation: convertToLocationModel(folder.location),
							serverRelativeUrl: folder.serverRelativeUrl,
							queryParameters: queryParameters
						},
						cancellation.signal
					);
				}

				setLoadingState(
					(x): LoadingState => {
						if (!x.isLoading && x.hasError) {
							console.error(`NotSuported LoadingState ${JSON.stringify(x)}`);
							return x;
						}

						const existingItems: readonly DocumentItemViewModel[] = pagePosition
							? x.retrievedData?.items ?? []
							: [];

						return {
							isLoading: false,
							retrievedData: {
								items: [...existingItems, ...page.items],
								nextPagePosition: page.pagerParameters
									.nextPagePosition
							}
						};
					}
				);

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				if (pagePosition) {
					setLoadingState(
						(x): LoadingState => {
							if (!x.isLoading || !x.retrievedData) {
								console.error(`NotSuported LoadingState ${JSON.stringify(x)}`);
								return x;
							}

							return {
								isLoading: false,
								retrievedData: {
									...x.retrievedData,
									nextPagePosition: null
								}
							};
						}
					);
				} else {
					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			}
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			return (): void => {
				loadPageCancellationRef.current
					?.abort();
			};
		},
		[]
	);

	useEffect(
		(): (() => void) | void => {
			if (!viewAndFolder.isSet
				|| viewAndFolder.value === null
			) {
				return;
			}

			loadPageHandler(
				viewAndFolder.value,
				searchTerm.value,
				null
			);
		},
		[
			loadPageHandler,
			searchTerm.value,
			viewAndFolder
		]
	);

	const searchTermChangeHandler = useCallback(
		(value: string, version: Guid): void => {
			setSearchTerm({
				value,
				version
			});
		},
		[]
	);

	const loadMoreHandler = useCallback(
		(
			viewAndFolder: ViewAndFolder,
			pagePosition: PagePosition
		): void => {
			loadPageHandler(
				viewAndFolder,
				searchTerm.value,
				pagePosition
			);
		},
		[loadPageHandler, searchTerm.value]
	);

	const renderContent = (): ReactElement | null => {
		if (!isSharePointConfigured) {
			return (
				<LiveTileContent.Message
					text={localize("_TheConnectionWithSharePointIsNotConfiguredThereforeWeCouldNotRetrieveYourDocuments")}
				/>
			);
		}

		if (!viewAndFolder.isSet) {
			return null;
		}

		if (viewAndFolder.value === null) {
			return <LiveTileContent.Message text={localize("_YouDontHaveThePermissionsRequiredToViewThisFolder")} />;
		}

		if (!loadingState.isLoading && loadingState.hasError) {
			return <LiveTileContent.Message text={localize("_UnknownErrorDuringRetrievalOfDocuments")} />;
		}

		const viewAndFolderValue: ViewAndFolder = viewAndFolder.value;

		const {
			view,
			folder
		} = viewAndFolderValue;

		if (!loadingState.retrievedData) {
			return null;
		}

		const {
			retrievedData: {
				items,
				nextPagePosition
			}
		} = loadingState;

		if (items.length === 0) {
			return (
				<LiveTileContent.Message
					text={localize(
						searchTerm.value
							? "_NoResultsFound"
							: "_NoItemsToShow"
					)}
				/>
			);
		}

		return (
			<>
				<LiveTileContent.List trackingId="sharepoint-items">
					{items.map((x): ReactElement => (
						<SharepointListItem
							key={x.id}
							trackingId={generateTrackingId(`|${x.id}`)}
							shouldShowQuickActions={shouldShowQuickActions}
							folder={folder}
							item={x}
							view={view}
							onAddAnalyticsEvent={onAddAnalyticsEvent}
							alertManager={alertManager}
							destinationWindow={destinationWindow}
							isTouch={isTouch}
						/>
					))}
				</LiveTileContent.List>

				{nextPagePosition && (
					<LiveTileContent.LoadMoreButton
						onClick={() => loadMoreHandler(viewAndFolderValue, nextPagePosition)}
					/>
				)}
			</>
		);
	};

	const isLoading: boolean = isSharePointConfigured
		&& (
			!viewAndFolder.isSet
			|| loadingState.isLoading
		);

	const hasError: boolean = (viewAndFolder.isSet && viewAndFolder === null)
		|| (!loadingState.isLoading && loadingState.hasError === true);

	return (
		<LiveTileContent.ContentStack>
			{isSharePointConfigured && !hasError && (
				<LiveTileContent.SearchInput
					placeholder={localize("_Search")}
					value={searchTerm}
					onChange={searchTermChangeHandler}
					trigger={{ kind: ChangeTriggerKind.Debounced }}
				/>
			)}

			<LiveTileContent.BusyIndicatorContainer>
				{isLoading && (
					<BusyIndicator />
				)}

				<LiveTileContent.Scrollable ref={scrollableRef}>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
SharepointFolderContent.displayName = "SharepointFolderContent";

const PAGE_SIZE = 10;

type ViewAndFolder = {
	view: DocumentsViewModel;
	folder: Folderish;
};

type RetrievedData = {
	items: readonly DocumentItemViewModel[];
	nextPagePosition: PagePosition | null;
};

type LoadingState =
	{
		isLoading: true;
		retrievedData?: RetrievedData;
	} |
	{
		isLoading: false;
	}
	& (
		{
			hasError: true;
		}
		|
		{
			hasError?: undefined;
			retrievedData: RetrievedData;
		}
	);

type SharepointListItemProps = {
	view: DocumentsViewModel;
	folder: Folderish;
	item: DocumentItemViewModel;
	trackingId: string;
	destinationWindow: DestinationWindow;
	alertManager: AlertManager;
	onAddAnalyticsEvent: () => void;
	shouldShowQuickActions: boolean;
	isTouch: boolean;
};

const SharepointListItem = (props: SharepointListItemProps): ReactElement => {
	const {
		view,
		folder,
		item,
		trackingId,
		destinationWindow,
		alertManager,
		shouldShowQuickActions,
		isTouch,
		onAddAnalyticsEvent
	} = props;

	const actionContext = useMemo(
		(): SharePointDocumentActionContext => ({
			view,
			folder,
			selection: [item],
			isSharingViaLinkEnabled: documentsConfiguration()
				.sharePoint
				.isSharingViaLinkEnabled,
			isVersioningEnabled: view.isVersioningEnabled,
			isInSearchResults: false,
			// Defaults which we don't care for in the live tile
			maxNumberOfFilesToDownloadAsZip: 0,
			syncClient: SyncClient.None,
			isMobile: false,
			isInSelectMode: false
		}),
		[
			folder,
			item,
			view
		]
	);

	const handlers = useMemo(
		(): OpenActionHandlers => ({
			openFolder: (folder) => {
				const url: string = getSharePointLocationUrl(folder.location, folder.id);
				navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
			},
			selectItem: () => { /* NOOP */ }
		}),
		[destinationWindow]
	);

	const actions = useMemo(
		(): readonly SharePointDocumentAction[] => {
			return getActionsForLiveTile(
				actionContext,
				handlers,
				alertManager
			).map(
				(action: SharePointDocumentAction): SharePointDocumentAction => ({
					...action,
					execute: (context: SharePointDocumentActionContext): void => {
						onAddAnalyticsEvent();
						action.execute(context);
					}
				})
			);
		},
		[
			actionContext,
			alertManager,
			handlers,
			onAddAnalyticsEvent
		]
	);

	const openItemHandler = useCallback(
		(): void => {
			onAddAnalyticsEvent();

			const openActions: readonly SharePointDocumentAction[] = getOpenActions(
				handlers,
				actionContext.selection,
				alertManager
			);

			reduceToAvailableActions(openActions, actionContext)[0]
				?.execute(actionContext);
		},
		[
			actionContext,
			alertManager,
			handlers,
			onAddAnalyticsEvent
		]
	);

	return (
		<DocumentListItem
			trackingId={trackingId}
			documentKind={item.itemType}
			extension={item.extension}
			name={item.name}
			actionContext={actionContext}
			onClick={openItemHandler}
			actions={actions}
			isTouch={isTouch}
			shouldShowQuickActions={shouldShowQuickActions}
		/>
	);
};
SharepointListItem.displayName = "SharepointListItem";
